/**
 * RecordResponseModal
 *
 * Modal for recording a lender's response to a submission.
 * Fields: responseType, responseSummary, conditionsReceived.
 *
 * Updates via PATCH /api/submissions/:id/response
 * F006 AC003
 */

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const RESPONSE_TYPE_OPTIONS = [
  { value: "approved", label: "Approved" },
  { value: "declined", label: "Declined" },
  { value: "conditional", label: "Conditional Approval" },
  { value: "more_info", label: "More Information Required" },
];

interface RecordResponseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  submissionId: string;
  applicationId: string;
}

export function RecordResponseModal({
  open,
  onOpenChange,
  submissionId,
  applicationId,
}: RecordResponseModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [responseType, setResponseType] = useState("approved");
  const [responseSummary, setResponseSummary] = useState("");
  const [conditionsReceived, setConditionsReceived] = useState(false);

  // Reset form when opening
  useEffect(() => {
    if (open) {
      setResponseType("approved");
      setResponseSummary("");
      setConditionsReceived(false);
    }
  }, [open]);

  const recordMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("PATCH", `/api/submissions/${submissionId}/response`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/applications/${applicationId}/submissions`],
      });
      toast({ title: "Response recorded" });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to record response",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    recordMutation.mutate({
      responseType,
      responseSummary: responseSummary || null,
      conditionsReceived,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="dialog-record-response">
        <DialogHeader>
          <DialogTitle>Record Lender Response</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Response Type */}
          <div className="space-y-1.5">
            <Label>Response Type</Label>
            <Select value={responseType} onValueChange={setResponseType}>
              <SelectTrigger data-testid="select-response-type">
                <SelectValue placeholder="Select response type" />
              </SelectTrigger>
              <SelectContent>
                {RESPONSE_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Response Summary */}
          <div className="space-y-1.5">
            <Label>Response Summary</Label>
            <Textarea
              placeholder="Summary of the lender's response..."
              value={responseSummary}
              onChange={(e) => setResponseSummary(e.target.value)}
              rows={4}
              data-testid="input-response-summary"
            />
          </div>

          {/* Conditions Received */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="conditions-received"
              checked={conditionsReceived}
              onCheckedChange={(checked) => setConditionsReceived(checked === true)}
              data-testid="checkbox-conditions-received"
            />
            <Label htmlFor="conditions-received" className="cursor-pointer">
              Conditions received with this response
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={recordMutation.isPending}
            data-testid="button-cancel-response"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={recordMutation.isPending}
            data-testid="button-submit-response"
          >
            {recordMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Record Response
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
